// @givmo/fundraisers-module — the creation wizard's own pieces (EMC-WEB7): the Givmo-blue stage and
// what sits on it, built to the approved frames 09b–09o. The stage spends the expression; the white
// field, card or photo is where the eye lands. Every colour is a design-system token (`brand-*`,
// `on-brand`, `surface`, `text-*`), and the depth and the motion are the package stylesheet's
// `fundraiser-wizard*` rules.
//
// One column, the same on every step (720px less its 24px gutters: 672px), so the step label, the
// question and the act never move as she goes. Every line on the blue is full white at weight 500 or
// more (white on brand-500 is 4.66:1).

import {
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
  type Ref,
  type RefObject,
} from 'react';
import { Dialog, GivmoWordmark, Icon, cn, testIdProp } from '@givmo/design-system';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CircleAlert,
  ImagePlus,
  Images,
  LoaderCircle,
  RefreshCw,
  X,
} from 'lucide-react';
import { COLLATERAL_COVER_COPY } from './collateral-cover';
import { WIZARD_COPY as C } from './creation-wizard-copy';

/** The white outline every stage control shows for keyboard focus, clear of the blue around it. */
const STAGE_FOCUS =
  'outline-none focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-on-brand';

// ── The stage ────────────────────────────────────────────────────────────────────────────────────

/**
 * The full-window stage: the design system's modal dialog at the window's size, so the console behind
 * it is out of reach (focus trapped, the page inert, its scroll locked) while toasts still speak. The
 * top bar carries the mark, the progress and the way out; "Skip to full editor" is a quiet link beside
 * "Save and exit", never beside the step's act (Jonathan, 2026-10-28). The dialog never dismisses
 * itself: there is nothing outside it to press, and Escape leaves her where she is.
 */
export function WizardStage({
  title = C.title,
  contentTop = 'pt-[120px] max-md:pt-48',
  step,
  total,
  exitLabel,
  onExit,
  onSkip,
  busy,
  initialFocus,
  children,
}: {
  /** The top bar's label and the dialog's name: "New fundraiser", or "Your fundraiser page". */
  title?: string;
  /** The space above the column: less where a header sits over the question. */
  contentTop?: string;
  step: number;
  /** Unknown before the choice: the bar then shows the steps taken, and the label "Step N". */
  total: number | null;
  exitLabel: string;
  onExit: () => void;
  /** "Skip to full editor": from the step that has a draft to the step before the last. */
  onSkip?: () => void;
  busy: boolean;
  initialFocus?: RefObject<HTMLElement | null>;
  children: ReactNode;
}) {
  return (
    <Dialog.Root open modal disablePointerDismissal onOpenChange={() => undefined}>
      <Dialog.Content
        fullScreen
        aria-label={title}
        initialFocus={initialFocus}
        className="fundraiser-wizard flex flex-col font-sans tracking-givmo text-on-brand"
        testId="fundraiser-wizard"
      >
        {/* Below `md` the label and the centred bar give way (the step label above the question
            carries the count), so the way out keeps its words at a phone's width. */}
        <header className="relative flex h-[76px] shrink-0 items-center justify-between gap-12 px-32 max-md:px-16">
          <div className="flex items-center gap-14">
            <GivmoWordmark className="h-[22px] w-auto text-on-brand" aria-hidden />
            <span className="h-[18px] w-px bg-on-brand/50 max-md:hidden" aria-hidden />
            <span className="text-s15 font-w600 text-on-brand max-md:hidden">{title}</span>
          </div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 max-md:hidden">
            <WizardProgress step={step} total={total} />
          </div>
          <div className="flex items-center gap-4">
            {onSkip ? (
              <>
                <TopBarButton onClick={onSkip} disabled={busy} testId="fundraiser-wizard-skip">
                  {C.skip}
                </TopBarButton>
                <span className="h-[18px] w-px bg-on-brand/50 max-sm:hidden" aria-hidden />
              </>
            ) : null}
            <TopBarButton onClick={onExit} disabled={busy} testId="fundraiser-wizard-exit">
              {exitLabel}
              <Icon icon={X} size={18} aria-hidden />
            </TopBarButton>
          </div>
        </header>
        <div
          className={cn(
            'relative mx-auto flex w-full max-w-[720px] flex-col px-24 pb-48 max-sm:px-16',
            contentTop,
          )}
        >
          {children}
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}

function TopBarButton({
  onClick,
  disabled,
  testId,
  children,
}: {
  onClick: () => void;
  disabled: boolean;
  testId: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'inline-flex h-[40px] items-center gap-8 rounded-all50 px-16 text-s15 font-w600 text-on-brand hover:bg-on-brand/10 disabled:cursor-not-allowed max-sm:min-h-44 max-sm:px-10',
        STAGE_FOCUS,
      )}
      {...testIdProp(testId)}
    >
      {children}
    </button>
  );
}

/**
 * Step N of M as segments: the steps taken and the current one filled, the rest at a quarter. Before
 * the choice there is no total, so the bar shows only the steps taken. A segment's fill grows from its
 * left edge, and a segment the choice adds grows its width in, so the bar extends from its centre.
 * Hidden from assistive technology: the step label above the question says the same in words.
 */
export function WizardProgress({ step, total }: { step: number; total: number | null }) {
  return (
    <div className="flex items-center" aria-hidden data-testid="fundraiser-wizard-progress">
      {Array.from({ length: total ?? step }, (_, index) => (
        // Spaced by margin, not gap, so a segment growing in brings its own space with it.
        <span
          key={index}
          className={cn(
            'fundraiser-wizard-segment relative h-[5px] w-[36px] overflow-hidden rounded-all50 bg-on-brand/25',
            index > 0 && 'ml-6',
          )}
        >
          <span
            className={cn(
              'fundraiser-wizard-segment-fill absolute inset-0 rounded-all50 bg-on-brand',
              index < step ? 'scale-x-100 opacity-100' : 'scale-x-0 opacity-0',
            )}
          />
        </span>
      ))}
    </div>
  );
}

// ── The question ─────────────────────────────────────────────────────────────────────────────────

/** A question never ends a line on "a", "an" or "the": each is bound to the word after it. */
export function keepShortWordsWithNext(text: string): string {
  return text.replace(/\b(a|an|the) (?=\S)/gi, '$1\u00a0');
}

/**
 * "Step N of M", the question, and at most one short line under it. The question is refined Lato
 * (Jonathan's pick, 2026-10-28): Lato's heaviest real cut (900), 48px, tracked −1.2px; one too long for
 * a line breaks into two balanced lines ("Who can create / a fundraiser page?").
 */
export function WizardQuestion({
  stepLabel,
  title,
  titleId,
  titleRef,
  line,
  lineId,
  stepTestId,
}: {
  stepLabel: string;
  title: string;
  titleId: string;
  /**
   * A step with no field to answer takes the focus at its question, so a screen reader hears it, with
   * its line as the description a field's would be.
   */
  titleRef?: Ref<HTMLHeadingElement>;
  line?: string;
  lineId?: string;
  /** Only the question on screen carries it, never the one leaving. */
  stepTestId?: string;
}) {
  return (
    <div className="flex flex-col">
      <p className="text-s15 font-w700 text-on-brand" {...testIdProp(stepTestId)}>
        {stepLabel}
      </p>
      <h1
        ref={titleRef}
        id={titleId}
        tabIndex={titleRef ? -1 : undefined}
        aria-describedby={titleRef && line ? lineId : undefined}
        className="mt-10 text-balance text-s48 font-w900 leading-[1.08] tracking-[-1.2px] text-on-brand outline-none max-sm:text-s34"
      >
        {keepShortWordsWithNext(title)}
      </h1>
      {line ? (
        <p
          id={lineId}
          className="mt-14 text-pretty text-s18 font-w500 leading-[1.45] text-on-brand"
        >
          {line}
        </p>
      ) : null}
    </div>
  );
}

// ── The field ────────────────────────────────────────────────────────────────────────────────────

const FIELD_RING =
  'focus-within:outline-solid focus-within:outline-4 focus-within:outline-offset-0 focus-within:outline-on-brand/40';

/** The white field the eye lands on: one line, large type, with an optional prefix ("$"). */
export function StageField({
  value,
  onChange,
  placeholder,
  prefix,
  inputMode,
  maxLength,
  labelledBy,
  describedBy,
  invalid,
  inputRef,
  testId,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  prefix?: string;
  inputMode?: 'text' | 'decimal';
  maxLength?: number;
  labelledBy: string;
  describedBy?: string;
  invalid?: boolean;
  inputRef?: Ref<HTMLInputElement>;
  testId?: string;
}) {
  return (
    <label
      className={cn(
        'fundraiser-wizard-lift flex h-[76px] items-center gap-10 rounded-all20 bg-surface px-28 max-sm:px-20',
        FIELD_RING,
      )}
    >
      {prefix ? (
        <span className="text-s28 font-w600 text-text-tertiary max-sm:text-s22" aria-hidden>
          {prefix}
        </span>
      ) : null}
      <input
        ref={inputRef}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        inputMode={inputMode}
        maxLength={maxLength}
        autoComplete="off"
        aria-labelledby={labelledBy}
        aria-describedby={describedBy}
        aria-invalid={invalid || undefined}
        className="min-w-0 flex-1 bg-transparent text-s28 font-w600 text-text-strong caret-brand-500 outline-none placeholder:font-w500 placeholder:text-text-tertiary/70 max-sm:text-s22"
        {...testIdProp(testId)}
      />
    </label>
  );
}

/** The story's white box: the same ring and lift, at the height the frame draws (236px). */
export function StageTextArea({
  value,
  onChange,
  maxLength,
  labelledBy,
  describedBy,
  inputRef,
  testId,
}: {
  value: string;
  onChange: (value: string) => void;
  maxLength: number;
  labelledBy: string;
  describedBy?: string;
  inputRef?: Ref<HTMLTextAreaElement>;
  testId?: string;
}) {
  return (
    <div className={cn('fundraiser-wizard-lift rounded-all20 bg-surface', FIELD_RING)}>
      <textarea
        ref={inputRef}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        maxLength={maxLength}
        aria-labelledby={labelledBy}
        aria-describedby={describedBy}
        className="block min-h-[236px] w-full resize-none rounded-all20 bg-transparent px-28 py-24 text-s19 leading-[1.6] text-text-strong caret-brand-500 outline-none"
        {...testIdProp(testId)}
      />
    </div>
  );
}

// ── The acts ─────────────────────────────────────────────────────────────────────────────────────

/** The row under the question: Back and the step's own act, nothing else, and a line when it needs one. */
export function WizardActions({
  onBack,
  backDisabled = false,
  backTestId,
  note,
  error,
  children,
}: {
  /** Absent on step 1. */
  onBack?: () => void;
  backDisabled?: boolean;
  /** Only the question on screen carries test ids, never the one leaving. */
  backTestId?: string;
  /** What the act does, when it can't be undone or where it lands: one legible line under the row. */
  note?: string;
  /** A refusal, said under the row in the server's words. */
  error?: string | null;
  children: ReactNode;
}) {
  return (
    <div className="mt-36 flex flex-col gap-18">
      <div className="flex items-center gap-12">
        {onBack ? (
          // The quiet button's padding sits outside the column, so its arrow lines up with the content.
          <span className="-ml-16">
            <StageButton
              tone="quiet"
              leading={ArrowLeft}
              onClick={onBack}
              disabled={backDisabled}
              testId={backTestId}
            >
              {C.back}
            </StageButton>
          </span>
        ) : null}
        <div className="ml-auto flex items-center gap-12">{children}</div>
      </div>
      {error ? (
        <p
          role="alert"
          className="flex items-start gap-8 text-s17 font-w600 leading-[1.5] text-on-brand"
          data-testid="fundraiser-wizard-error"
        >
          <Icon icon={CircleAlert} size={20} className="mt-2 shrink-0" aria-hidden />
          {error}
        </p>
      ) : null}
      {note ? (
        <p className="text-pretty text-s17 font-w500 leading-[1.5] text-on-brand">{note}</p>
      ) : null}
    </div>
  );
}

/** The stage's buttons: white for the act (pale while it waits for an answer), quiet for Back. */
export function StageButton({
  tone = 'primary',
  type = 'button',
  disabled = false,
  busy = false,
  leading,
  trailing,
  onClick,
  testId,
  children,
}: {
  tone?: 'primary' | 'quiet';
  type?: 'button' | 'submit';
  disabled?: boolean;
  /** Saving: the act keeps its white face and turns its arrow into a spinner, and takes no second press. */
  busy?: boolean;
  leading?: typeof ArrowLeft;
  trailing?: typeof ArrowRight;
  onClick?: () => void;
  testId?: string;
  children: ReactNode;
}) {
  const pale = tone === 'primary' && disabled && !busy;
  return (
    <button
      type={type}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      onClick={onClick}
      className={cn(
        'inline-flex h-[52px] items-center gap-8 rounded-all50 text-s17 font-w700 transition-colors duration-200',
        STAGE_FOCUS,
        tone === 'primary' &&
          (pale
            ? 'cursor-not-allowed bg-on-brand/30 px-28 text-on-brand/75'
            : 'fundraiser-wizard-lift bg-surface px-28 text-brand-700'),
        tone === 'quiet' && 'px-16 text-on-brand hover:bg-on-brand/10 disabled:cursor-not-allowed',
      )}
      {...testIdProp(testId)}
    >
      {leading ? <Icon icon={leading} size={20} aria-hidden /> : null}
      {children}
      {busy ? (
        <Icon icon={LoaderCircle} size={20} className="motion-safe:animate-spin" aria-hidden />
      ) : trailing ? (
        <Icon icon={trailing} size={20} aria-hidden />
      ) : null}
    </button>
  );
}

/** Next, with its arrow. */
export const NEXT_ARROW = ArrowRight;

// ── The choices ──────────────────────────────────────────────────────────────────────────────────

/** The ring a choice wears: white and eased out from the card when chosen, and for keyboard focus. */
function choiceRing(chosen: boolean, offset: 'card' | 'row') {
  return cn(
    'fundraiser-wizard-select outline-solid outline-[3px]',
    chosen
      ? cn('outline-on-brand', offset === 'card' ? 'outline-offset-[5px]' : 'outline-offset-[4px]')
      : 'outline-offset-[1px] outline-transparent',
    // Focus without a choice yet (Tab into a group with nothing chosen) rings the card at 60%; focus on
    // the chosen one thickens its ring, so focus always shows.
    chosen
      ? 'has-[:focus-visible]:outline-[5px]'
      : cn(
          'has-[:focus-visible]:outline-on-brand/60',
          offset === 'card'
            ? 'has-[:focus-visible]:outline-offset-[5px]'
            : 'has-[:focus-visible]:outline-offset-[4px]',
        ),
  );
}

/** A choice's mark: an empty ring, with a filled check that grows in when it is chosen. */
function ChoiceMark({ chosen }: { chosen: boolean }) {
  return (
    <span
      className="relative size-28 shrink-0 rounded-all100 border-2 border-neutral-300"
      aria-hidden
    >
      <span
        className={cn(
          'fundraiser-wizard-check absolute -inset-[2px] flex items-center justify-center rounded-all100 bg-brand-500 text-on-brand',
          chosen ? 'scale-100 opacity-100' : 'scale-50 opacity-0',
        )}
      >
        <Icon icon={Check} size={18} aria-hidden />
      </span>
    </span>
  );
}

/**
 * One of the two cards at a fork: art (the kind's cards; the groups question's are plain), a label and
 * one short line, over a native radio, so the pair is a real radio group (arrow keys move and choose;
 * the label names it and the line describes it).
 */
export function ChoiceCard({
  name,
  value,
  art,
  label,
  line,
  chosen,
  disabled = false,
  onSelect,
  inputRef,
  testId,
}: {
  name: string;
  value: string;
  art?: ReactNode;
  label: string;
  line: string;
  chosen: boolean;
  disabled?: boolean;
  onSelect: () => void;
  inputRef?: Ref<HTMLInputElement>;
  testId?: string;
}) {
  const labelId = useId();
  const lineId = useId();
  return (
    <label
      className={cn(
        'fundraiser-wizard-lift relative flex flex-1 flex-col overflow-hidden rounded-all20 bg-surface text-text-strong',
        choiceRing(chosen, 'card'),
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
      )}
    >
      <input
        ref={inputRef}
        type="radio"
        name={name}
        value={value}
        checked={chosen}
        disabled={disabled}
        onChange={onSelect}
        aria-labelledby={labelId}
        aria-describedby={lineId}
        className="sr-only"
        {...testIdProp(testId)}
      />
      {art ? <div className="h-[176px] bg-brand-50">{art}</div> : null}
      <div className="flex flex-col gap-8 px-24 pt-22 pb-26">
        <div className="flex items-center justify-between gap-12">
          <span id={labelId} className="text-s22 font-w700 text-text-strong">
            {label}
          </span>
          <ChoiceMark chosen={chosen} />
        </div>
        <p id={lineId} className="text-pretty text-s16 leading-[1.5] text-text-secondary">
          {line}
        </p>
      </div>
    </label>
  );
}

/** A row in a stacked list of choices (who can create a supporter page), over a native radio. */
export function OptionRow({
  name,
  value,
  label,
  line,
  chosen,
  onSelect,
  inputRef,
  testId,
}: {
  name: string;
  value: string;
  label: string;
  line: string;
  chosen: boolean;
  onSelect: () => void;
  inputRef?: Ref<HTMLInputElement>;
  testId?: string;
}) {
  const labelId = useId();
  const lineId = useId();
  return (
    <label
      className={cn(
        'fundraiser-wizard-lift flex cursor-pointer items-center gap-18 rounded-all20 bg-surface px-24 py-20 text-text-strong',
        choiceRing(chosen, 'row'),
      )}
    >
      <input
        ref={inputRef}
        type="radio"
        name={name}
        value={value}
        checked={chosen}
        onChange={onSelect}
        aria-labelledby={labelId}
        aria-describedby={lineId}
        className="sr-only"
        {...testIdProp(testId)}
      />
      <span className="flex min-w-0 flex-1 flex-col gap-4">
        <span id={labelId} className="text-s18 font-w700">
          {label}
        </span>
        <span id={lineId} className="text-pretty text-s15 leading-[1.45] text-text-secondary">
          {line}
        </span>
      </span>
      <ChoiceMark chosen={chosen} />
    </label>
  );
}

// ── Set up your groups ───────────────────────────────────────────────────────────────────────────

/**
 * "Set up your groups": its three questions in one white card (Jonathan's ruling, 2026-10-29), each a
 * section under its own heading, divided by a hairline.
 */
export function SetupCard({ children }: { children: ReactNode }) {
  return (
    <div className="fundraiser-wizard-lift flex flex-col rounded-all20 bg-surface px-28 py-22 text-text-strong max-sm:px-20">
      {children}
    </div>
  );
}

export function SetupSection({
  title,
  titleId,
  divided = false,
  children,
}: {
  title: string;
  titleId: string;
  divided?: boolean;
  children: ReactNode;
}) {
  return (
    <section
      aria-labelledby={titleId}
      className={cn('flex flex-col gap-12', divided && 'mt-18 border-t border-border-subtle pt-18')}
    >
      <h2 id={titleId} className="text-s16 font-w700 text-text-strong">
        {title}
      </h2>
      {children}
    </section>
  );
}

/**
 * A small answer inside the setup card, over a native radio: brand-tinted with a filled dot when
 * chosen, and ringed for keyboard focus. At most one short line under its label.
 */
export function SmallOption({
  name,
  value,
  label,
  line,
  chosen,
  onSelect,
  testId,
}: {
  name: string;
  value: string;
  label: string;
  line?: string;
  chosen: boolean;
  onSelect: () => void;
  testId?: string;
}) {
  const labelId = useId();
  const lineId = useId();
  return (
    <label
      className={cn(
        'flex min-h-[56px] cursor-pointer gap-10 rounded-all12 border px-14 py-12 transition-colors duration-150',
        'has-[:focus-visible]:outline-solid has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-brand-500',
        line === undefined ? 'items-center' : 'items-start',
        chosen ? 'border-brand-500 bg-brand-50' : 'border-neutral-300 hover:border-neutral-500',
      )}
    >
      <input
        type="radio"
        name={name}
        value={value}
        checked={chosen}
        onChange={onSelect}
        aria-labelledby={labelId}
        aria-describedby={line === undefined ? undefined : lineId}
        className="sr-only"
        {...testIdProp(testId)}
      />
      <span
        className={cn(
          'flex size-20 shrink-0 items-center justify-center rounded-all100 border-2',
          line === undefined ? '' : 'mt-1',
          chosen ? 'border-brand-500' : 'border-neutral-300',
        )}
        aria-hidden
      >
        <span
          className={cn(
            'size-10 rounded-all100 bg-brand-500 transition-transform duration-150',
            chosen ? 'scale-100' : 'scale-0',
          )}
        />
      </span>
      <span className="flex min-w-0 flex-col gap-4">
        <span id={labelId} className="text-s16 font-w700 leading-[1.35] text-text-strong">
          {label}
        </span>
        {line === undefined ? null : (
          <span id={lineId} className="text-pretty text-s14 leading-[1.4] text-text-secondary">
            {line}
          </span>
        )}
      </span>
    </label>
  );
}

// ── The art on the choice cards ──────────────────────────────────────────────────────────────────

/** A small fundraiser page: cover (a sun and two hills, so it reads as a photo), title, bar, Donate. */
function MiniPage({
  width,
  fill,
  className,
  tilt,
  avatar,
}: {
  width: number;
  /** How much of the bar is filled, 0–1. */
  fill: number;
  className?: string;
  tilt?: number;
  avatar?: { initials: string; tone: 'deep' | 'light'; side: 'left' | 'right' };
}) {
  return (
    <div
      className={cn(
        'absolute flex flex-col overflow-visible rounded-all12 border border-brand-100 bg-surface p-8 shadow-[0_10px_22px_-12px_color-mix(in_srgb,var(--color-brand-950)_35%,transparent)]',
        className,
      )}
      style={{ width, transform: tilt ? `rotate(${tilt}deg)` : undefined }}
    >
      <div
        className="relative overflow-hidden rounded-all7 bg-brand-200"
        style={{ height: Math.round(width * 0.42) }}
      >
        <span className="absolute top-[20%] right-[18%] aspect-square w-[15%] rounded-all100 bg-surface/80" />
        <span className="absolute -bottom-[30%] -left-[10%] h-[70%] w-[75%] rounded-all100 bg-brand-300" />
        <span className="absolute right-[-15%] -bottom-[40%] h-[75%] w-[70%] rounded-all100 bg-brand-400/70" />
      </div>
      <div className="mt-8 h-[7px] w-[72%] rounded-all50 bg-brand-300" />
      <div className="mt-5 h-[5px] w-[48%] rounded-all50 bg-brand-100" />
      <div className="mt-8 h-[5px] w-full overflow-hidden rounded-all50 bg-brand-100">
        <div className="h-full rounded-all50 bg-brand-500" style={{ width: `${fill * 100}%` }} />
      </div>
      <div className="mt-8 h-[14px] w-full rounded-all50 bg-brand-500" />
      {avatar ? (
        <span
          className={cn(
            'absolute -top-10 flex size-28 items-center justify-center rounded-all100 border-2 border-surface text-s10 font-w700 text-on-brand',
            avatar.side === 'left' ? '-left-10' : '-right-10',
            avatar.tone === 'deep' ? 'bg-brand-700' : 'bg-brand-600',
          )}
        >
          {avatar.initials}
        </span>
      ) : null}
    </div>
  );
}

/** "Just this page": one page, rising out of the tile. */
export function OnePageArt() {
  return (
    <div className="relative h-full overflow-hidden" aria-hidden>
      <MiniPage width={128} fill={0.42} className="top-[22px] left-1/2 -translate-x-1/2" />
    </div>
  );
}

/** "A campaign": its page in front, people's own pages under it on either side. */
export function CampaignArt() {
  return (
    <div className="relative h-full overflow-hidden" aria-hidden>
      <MiniPage
        width={96}
        fill={0.3}
        className="top-[44px] left-[calc(50%-140px)]"
        tilt={-8}
        avatar={{ initials: 'DW', tone: 'deep', side: 'left' }}
      />
      <MiniPage
        width={96}
        fill={0.55}
        className="top-[44px] left-[calc(50%+44px)]"
        tilt={8}
        avatar={{ initials: 'MB', tone: 'light', side: 'right' }}
      />
      <MiniPage width={128} fill={0.62} className="top-[22px] left-1/2 z-10 -translate-x-1/2" />
    </div>
  );
}

// ── The cover photo ──────────────────────────────────────────────────────────────────────────────

/**
 * The cover as a 16:9 frame on a white card: a target to choose a photo from or drag one onto, the
 * photo uploading, or the photo with "Replace". It frames the photo as the editor does, 30% down from
 * the top (`COVER_FRAME_CLASS`), since faces sit high. Choosing opens the file picker; the parent
 * checks the file and uploads it. An upload swaps the frame's button for its status and back, so the
 * focus goes with it: to the status while it uploads, then to the frame's button again ("Replace",
 * or the target after a refusal), never left on the page behind.
 */
export function CoverFrame({
  src,
  uploading,
  accept,
  onFile,
  onCollateral,
  labelledBy,
  describedBy,
  buttonRef,
  testIds = true,
}: {
  /** The photo to show, once one is chosen. */
  src: string | null;
  uploading: boolean;
  accept: string;
  onFile: (file: File) => void;
  /** Opens the organization's Collateral, beside the upload; absent where it holds no image. */
  onCollateral?: () => void;
  labelledBy: string;
  describedBy?: string;
  buttonRef?: Ref<HTMLButtonElement>;
  /** False on the question leaving, so only the one on screen carries test ids. */
  testIds?: boolean;
}) {
  const id = (name: string) => (testIds ? name : undefined);
  const input = useRef<HTMLInputElement>(null);
  const status = useRef<HTMLSpanElement>(null);
  const button = useRef<HTMLButtonElement | null>(null);
  const wasUploading = useRef(uploading);
  const [dragging, setDragging] = useState(false);
  const typesId = useId();
  const take = (file: File | undefined) => {
    if (file && !uploading) onFile(file);
  };
  useEffect(() => {
    if (uploading) status.current?.focus({ preventScroll: true });
    else if (wasUploading.current) button.current?.focus({ preventScroll: true });
    wasUploading.current = uploading;
  }, [uploading]);
  /** The frame's one button at a time, which is also the step's focus target the parent registers. */
  const frameButton = (element: HTMLButtonElement | null) => {
    button.current = element;
    if (typeof buttonRef === 'function') buttonRef(element);
    else if (buttonRef) buttonRef.current = element;
  };
  return (
    <div className="fundraiser-wizard-lift rounded-all20 bg-surface p-12">
      <div className="relative aspect-[16/9] w-full overflow-hidden rounded-all12">
        {src === null ? (
          <button
            ref={frameButton}
            type="button"
            onClick={() => input.current?.click()}
            onDragOver={(event) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);
              take(event.dataTransfer.files[0]);
            }}
            aria-labelledby={labelledBy}
            aria-describedby={[describedBy, typesId].filter(Boolean).join(' ')}
            className={cn(
              'flex h-full w-full flex-col items-center justify-center gap-14 rounded-all12 border-2 border-dashed bg-brand-50 outline-none focus-visible:outline-solid focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand-500',
              dragging ? 'border-brand-500' : 'border-brand-300',
            )}
            data-testid={id('fundraiser-wizard-cover-choose')}
          >
            <span className="flex size-[64px] items-center justify-center rounded-all100 bg-surface text-brand-500 shadow-sm">
              <Icon icon={ImagePlus} size={28} aria-hidden />
            </span>
            <span className="text-s18 font-w600 text-text-strong">
              <span className="text-brand-700 underline decoration-brand-300 underline-offset-[4px]">
                {C.choosePhoto}
              </span>{' '}
              {C.orDrag}
            </span>
            <span id={typesId} className="text-s14 text-text-tertiary">
              {C.coverTypes}
            </span>
          </button>
        ) : (
          <>
            <img
              src={src}
              alt="Fundraiser cover"
              className="h-full w-full object-cover object-[50%_30%]"
            />
            {uploading ? (
              <div className="absolute inset-0 flex items-center justify-center bg-text-strong/45">
                <span
                  ref={status}
                  role="status"
                  tabIndex={-1}
                  className="inline-flex items-center gap-10 rounded-all50 bg-surface px-20 py-12 text-s16 font-w600 text-text-strong shadow-md outline-none"
                  data-testid={id('fundraiser-wizard-cover-uploading')}
                >
                  <Icon
                    icon={LoaderCircle}
                    size={20}
                    className="text-brand-500 motion-safe:animate-spin"
                    aria-hidden
                  />
                  {C.uploading}
                </span>
              </div>
            ) : (
              <button
                ref={frameButton}
                type="button"
                onClick={() => input.current?.click()}
                className="absolute top-14 right-14 inline-flex items-center gap-8 rounded-all50 bg-surface px-16 py-10 text-s15 font-w600 text-text-strong shadow-md outline-none focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
                data-testid={id('fundraiser-wizard-cover-replace')}
              >
                <Icon icon={RefreshCw} size={16} className="text-text-secondary" aria-hidden />
                {C.replace}
              </button>
            )}
          </>
        )}
      </div>
      <input
        ref={input}
        type="file"
        accept={accept}
        className="hidden"
        disabled={uploading}
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          take(file);
        }}
        data-testid={id('fundraiser-wizard-cover-input')}
      />
      {onCollateral && (
        <div className="mt-12 flex justify-center">
          <button
            type="button"
            onClick={onCollateral}
            disabled={uploading}
            className="inline-flex min-h-44 items-center gap-8 rounded-all50 px-16 text-s15 font-w600 text-brand-700 outline-none hover:bg-brand-50 focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 disabled:opacity-50"
            data-testid={id('fundraiser-wizard-cover-collateral')}
          >
            <Icon icon={Images} size={18} aria-hidden />
            {COLLATERAL_COVER_COPY.choose}
          </button>
        </div>
      )}
    </div>
  );
}
